import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import './style.scss';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { registrationActions } from 'careand-redux/slices/registration';
import { RegistrationBottomNavigation } from 'scenes/PatientRegistration/Navigation';
import Icon from 'components/Utils/NewIcon';
import DefaultPatientTextField from 'components/Utils/DefaultPatientTextField';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import { personalDetailsSchema, PersonalDetailsValues } from 'types/forms';

const PersonalDetails = ({
  track,
  onSuccess,
  goToStart,
  loading,
}: {
  track: (event: string) => void;
  onSuccess: () => void;
  goToStart: () => void;
  loading?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const registration = useAppSelector((state) => state.registration);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
  } = useForm<PersonalDetailsValues>({
    resolver: zodResolver(personalDetailsSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      date_of_birth: '' as any,
      gender: '' as any,
      ...(registration.personalData || {}),
      preferred_name: registration.preferred_name || '',
      preferred_pronoun: registration.preferred_pronoun || '',
    },
    mode: 'onChange',
  });

  const gender = watch('gender');

  useEffect(() => {
    track('registrationPersonal');
  }, [track]);

  useEffect(() => {
    if (!registration?.credentials?.username) {
      goToStart();
    }
  }, [registration]);

  function saveData(values: PersonalDetailsValues) {
    const name = values.preferred_name;
    const pronoun = values.preferred_pronoun;

    const personalData = {
      first_name: values.first_name,
      last_name: values.last_name,
      date_of_birth: values.date_of_birth,
      gender: values.gender,
    };

    dispatch(registrationActions.saveRegistrationPreferredName(name));
    dispatch(registrationActions.saveRegistrationPreferredPronoun(pronoun));
    dispatch(registrationActions.saveRegistrationPersonalData(personalData));
    onSuccess();
  }

  return (
    <div className="personal-details step">
      <span className="top-text small">Enter the patient's personal information</span>
      <form
        id="personal-details-form"
        className="personal-details-form"
        onSubmit={handleSubmit(saveData)}
      >
        <DefaultPatientTextField name="first_name" control={control} label="First Name" />
        <DefaultPatientTextField name="last_name" control={control} label="Last Name" />
        <DefaultPatientTextField
          name="preferred_name"
          control={control}
          label="Preferred Name"
          optional
        />
        <DefaultPatientTextField
          name="preferred_pronoun"
          control={control}
          label="Preferred Pronoun"
          optional
        />

        <span className="form-label">Date of Birth</span>
        <DefaultDatePicker
          id="date-field"
          name="date_of_birth"
          control={control}
          style={{ width: '100%' }}
          bordered
        />
        <span className="form-label">Gender</span>
        <div className="gender-selection">
          <CheckGreen
            name="Male"
            selected={gender === 'Male'}
            onChange={() => setValue('gender', 'Male', { shouldValidate: true })}
          />
          <CheckGreen
            name="Female"
            selected={gender === 'Female'}
            onChange={() => setValue('gender', 'Female', { shouldValidate: true })}
          />
        </div>

        <RegistrationBottomNavigation
          id="personal-details-submit"
          disabled={!isValid}
          loading={loading}
        />
      </form>
    </div>
  );
};

export const CheckGreen = ({
  name,
  secondLine,
  selected,
  onChange,
}: {
  name: string;
  secondLine?: string;
  selected: boolean;
  onChange: () => void;
}) => {
  let className = 'check-green';
  if (selected) className += ' selected';
  return (
    <div className={className} onClick={onChange}>
      <Icon src={selected ? 'check-circle' : 'circle'} width="20px" height="20px" />
      <div className="text">
        <span>{name}</span>
        {secondLine && <span className="second-line">{secondLine}</span>}
      </div>
    </div>
  );
};

export default PersonalDetails;
